import { Box, Button, HStack, Text } from '@chakra-ui/react';
import { t } from 'i18next';
import { DateTime, Interval } from 'luxon';
import { RelativeTime } from '~components/ui/RelativeTime';
import { AlertsMenu_AccessRequestFragment } from './__generated__/AlertsMenu.graphql';
import { formatRequestedDuration, getRequesterName } from './useAwaitingAccessRequests';

type Props = {
  accessRequest: AlertsMenu_AccessRequestFragment;
  onViewDetail: () => void;
};

/**
 * An awaiting access request shown among the organization's alerts. It is not stored as an alert:
 * it leaves the list once the request is accepted or expires.
 */
export function AccessRequestAlertsItem({ accessRequest, onViewDetail }: Props) {
  const name = getRequesterName(accessRequest.requestingServiceAdmin);
  const isNew = isNewAccessRequest(accessRequest);

  return (
    <Box
      role="group"
      p="2.5"
      borderRadius="md"
      backgroundColor={isNew ? '#F1F8FE' : undefined}
      display="flex"
      flexDir="row"
      w="full"
      justifyContent="space-between"
    >
      <Box flex="1">
        <Text color="#0A3761">
          {t('accessRequestAlert', {
            name,
            duration: formatRequestedDuration(accessRequest.requestedDurationMinutes),
          })}
        </Text>
        <RelativeTime
          ts={accessRequest.createdAt}
          render={(t) => (
            <Text fontSize="sm" color="gray.300">
              {t}
            </Text>
          )}
        />
      </Box>

      <HStack minWidth="230px" justifyContent="flex-end" alignItems="flex-end">
        <Button variant="ghost" color="#4B9EEC" size="sm" onClick={onViewDetail}>
          {t('viewDetail')}
        </Button>
      </HStack>
    </Box>
  );
}

function isNewAccessRequest(accessRequest: AlertsMenu_AccessRequestFragment) {
  const interval = Interval.fromDateTimes(
    DateTime.fromISO(accessRequest.createdAt),
    DateTime.now(),
  );
  const minutesSince = Math.round(interval.length('minutes'));
  return minutesSince < 5;
}
